import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button, Select, Input,
  DatePicker, message, Popconfirm
} from 'antd';
import {
  DatabaseOutlined, SearchOutlined, DownloadOutlined, DeleteOutlined, ReloadOutlined
} from '@ant-design/icons';
import { getRawScans, getEvents, getReaders, deleteRawScans } from '../api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const SOURCE_COLOR = { fixed: 'blue', handheld: 'green', simulator: 'orange' };

export default function RawInventory() {
  const [scans, setScans]         = useState([]);
  const [events, setEvents]       = useState([]);
  const [readers, setReaders]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [filterEvent, setFilterEvent]   = useState(null);
  const [filterReader, setFilterReader] = useState(null);
  const [filterSource, setFilterSource] = useState(null);
  const [filterTag, setFilterTag]       = useState('');
  const [dateRange, setDateRange]       = useState(null);

  const load = (pg = page) => {
    setLoading(true);
    const params = {
      event_id:    filterEvent  || undefined,
      reader_id:   filterReader || undefined,
      scan_source: filterSource || undefined,
      rfid_tag:    filterTag    || undefined,
      from_date:   dateRange?.[0]?.format('YYYY-MM-DD'),
      to_date:     dateRange?.[1]?.format('YYYY-MM-DD'),
      page: pg, limit: 100,
    };
    getRawScans(params).then(r => { setScans(r.data.data || []); setTotal(r.data.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    getEvents().then(r => {
      setEvents(r.data.data || []);
      const active = (r.data.data || []).find(e => e.status === 'active');
      if (active) setFilterEvent(active.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (filterEvent) getReaders({ event_id: filterEvent }).then(r => setReaders(r.data.data || [])).catch(() => {});
  }, [filterEvent]);

  useEffect(() => { setPage(1); load(1); }, [filterEvent, filterReader, filterSource, filterTag, dateRange]); // eslint-disable-line

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Raw RFID Data');
    ws.addRow(['#', 'RFID Tag', 'Delegate Name', 'Reader', 'Location', 'RSSI', 'Antenna', 'Source', 'Scanned At']);
    ws.getRow(1).font = { bold: true };
    ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1D6B' } };
    ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    scans.forEach((s, i) => ws.addRow([
      i + 1, s.rfid_tag, s.delegate_name || '', s.reader_name || '', s.location_name || '',
      s.rssi, s.antenna_port, s.scan_source, dayjs(s.scanned_at).format('DD-MMM-YY HH:mm:ss')
    ]));
    ws.columns.forEach(c => { c.width = 22; });
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `raw_rfid_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success('Excel exported');
  };

  const handleClear = async () => {
    try {
      const r = await deleteRawScans({ event_id: filterEvent });
      message.success(r.data.message || 'Cleared');
      load();
    } catch (e) { message.error('Delete failed'); }
  };

  const columns = [
    { title: '#', width: 60, render: (_, __, i) => (page - 1) * 100 + i + 1 },
    { title: 'RFID Tag', dataIndex: 'rfid_tag', render: v => <Text code style={{ fontSize: 10 }}>{v}</Text>, ellipsis: true },
    { title: 'Delegate', dataIndex: 'delegate_name', render: v => v || <Text type="secondary">Unknown</Text>, width: 160 },
    { title: 'Reader', dataIndex: 'reader_name', render: v => v || '-', width: 140 },
    { title: 'Location', dataIndex: 'location_name', render: v => v || '-', width: 140 },
    { title: 'RSSI', dataIndex: 'rssi', render: v => v ? `${v} dBm` : '-', width: 90, align: 'center' },
    { title: 'Ant.', dataIndex: 'antenna_port', width: 60, align: 'center' },
    { title: 'Source', dataIndex: 'scan_source', render: v => <Tag color={SOURCE_COLOR[v] || 'default'}>{v}</Tag>, width: 100 },
    { title: 'Scanned At', dataIndex: 'scanned_at', render: v => dayjs(v).format('DD MMM HH:mm:ss'), width: 150 },
  ];

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <DatabaseOutlined style={{ marginRight: 8 }} />Raw Inventory
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Raw RFID scan data from all readers — Bib/Tag, timestamp, reader</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={exportExcel}>Export Excel</Button>
            <Button icon={<ReloadOutlined />} onClick={() => load()}>Refresh</Button>
            <Popconfirm title="Clear all scans for this event?" onConfirm={handleClear}>
              <Button danger icon={<DeleteOutlined />}>Clear Data</Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 10 }}>
        <Row gutter={[12, 8]} align="middle">
          <Col>
            <Select value={filterEvent} onChange={v => { setFilterEvent(v); setFilterReader(null); }} style={{ width: 200 }} placeholder="Event" allowClear>
              {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
            </Select>
          </Col>
          <Col>
            <Select value={filterReader} onChange={setFilterReader} style={{ width: 180 }} placeholder="Reader" allowClear>
              {readers.map(r => <Option key={r.id} value={r.id}>{r.reader_name}</Option>)}
            </Select>
          </Col>
          <Col>
            <Select value={filterSource} onChange={setFilterSource} style={{ width: 130 }} placeholder="Source" allowClear>
              {['fixed', 'handheld', 'simulator'].map(s => <Option key={s} value={s}><Tag color={SOURCE_COLOR[s]}>{s}</Tag></Option>)}
            </Select>
          </Col>
          <Col>
            <Input prefix={<SearchOutlined />} placeholder="RFID Tag search"
              value={filterTag} onChange={e => setFilterTag(e.target.value)}
              style={{ width: 200 }} allowClear />
          </Col>
          <Col>
            <RangePicker onChange={setDateRange} style={{ width: 240 }} />
          </Col>
          <Col flex="1" />
          <Col>
            <Tag style={{ fontSize: 12, padding: '4px 10px' }}>Total: {total.toLocaleString()} scans</Tag>
          </Col>
        </Row>
      </Card>

      {/* Stats */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {['fixed', 'handheld', 'simulator'].map(s => (
          <Col key={s} xs={8}>
            <Card size="small" style={{ borderRadius: 8, textAlign: 'center', borderTop: `3px solid ${SOURCE_COLOR[s] === 'blue' ? '#1890ff' : SOURCE_COLOR[s] === 'green' ? '#52c41a' : '#fa8c16'}` }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{scans.filter(sc => sc.scan_source === s).length}</div>
              <Tag color={SOURCE_COLOR[s]} style={{ margin: 0 }}>{s}</Tag>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <Table
          columns={columns}
          dataSource={scans}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            pageSize: 100,
            total,
            onChange: (pg) => { setPage(pg); load(pg); },
            showTotal: (t) => `${t} total scans`,
            showSizeChanger: false,
          }}
        />
      </Card>
    </div>
  );
}
